import { useState } from 'react'
import { Plus, FileText } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import {
  Button,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  SearchInput,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
} from '@/components/ui'
import type { ColumnDef } from '@tanstack/react-table'
import { useIncidents, useIncidentStats } from '../api/queries'
import { IncidentStatusChip } from '../components/IncidentStatusChip'
import type { IncidentReport, IncidentType } from '../api/types'
import { INCIDENT_TYPE_LABELS } from '../api/types'

function fmtDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString()
}

function TypeChips({ types }: { types: IncidentType[] }) {
  if (!types || types.length === 0) return <span className="text-secondary">—</span>
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((t) => (
        <span
          key={t}
          className="text-xs rounded px-1.5 py-0.5 bg-surface-raised border border-border-default"
        >
          {INCIDENT_TYPE_LABELS[t] ?? t}
        </span>
      ))}
    </div>
  )
}

const COLUMNS: ColumnDef<IncidentReport>[] = [
  {
    accessorKey: 'report_number',
    header: 'Report #',
    size: 130,
    cell: ({ getValue }) => (
      <span className="font-mono text-sm">{getValue() as string}</span>
    ),
  },
  {
    accessorKey: 'date_of_incident',
    header: 'Incident Date',
    size: 120,
    cell: ({ getValue }) => fmtDate(getValue() as string | null),
  },
  {
    accessorKey: 'incident_types',
    header: 'Type(s)',
    size: 240,
    cell: ({ getValue }) => <TypeChips types={getValue() as IncidentType[]} />,
  },
  {
    accessorKey: 'location_in_warehouse',
    header: 'Location',
    size: 200,
    cell: ({ getValue }) => (getValue() as string) || <span className="text-secondary">—</span>,
  },
  {
    accessorKey: 'reported_by_name',
    header: 'Reported By',
    size: 150,
    cell: ({ getValue }) => (getValue() as string) || <span className="text-secondary">—</span>,
  },
  {
    accessorKey: 'facility_site',
    header: 'Facility',
    size: 130,
    cell: ({ getValue }) => (getValue() as string) || <span className="text-secondary">—</span>,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 100,
    cell: ({ getValue }) => (
      <IncidentStatusChip status={getValue() as IncidentReport['status']} />
    ),
  },
  {
    id: 'pdf',
    header: 'PDF',
    size: 60,
    cell: ({ row }) =>
      row.original.pdf_file_id ? (
        <a
          href={`/api/exports/${row.original.pdf_file_id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-brand hover:underline"
          title="Download PDF"
        >
          <FileText size={16} />
        </a>
      ) : (
        <span className="text-secondary">—</span>
      ),
  },
]

export function WarehouseIncidentsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const { data, isLoading, isError, error, refetch } = useIncidents({
    search: search || undefined,
    status: statusFilter || undefined,
  })
  const { data: stats } = useIncidentStats()

  const rows = data?.items ?? []

  return (
    <div className="flex flex-col h-full">
      {/* KPI strip */}
      {stats && (
        <MetricStrip>
          <MetricCard label="Total" value={stats.total} />
          <MetricCard label="Draft" value={stats.draft} />
          <MetricCard label="Submitted" value={stats.submitted} />
          <MetricCard label="Reviewed" value={stats.reviewed} />
        </MetricStrip>
      )}

      {/* Search bar — own full-width row */}
      <Toolbar>
        <ToolbarLeft>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search report #, location, reporter…"
            containerClassName="flex-1 max-w-none"
          />
        </ToolbarLeft>
      </Toolbar>

      {/* Controls */}
      <Toolbar>
        <ToolbarLeft>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="reviewed">Reviewed</option>
          </Select>
        </ToolbarLeft>
        <ToolbarRight>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate({ to: '/warehouse-incidents/new' })}
          >
            <Plus size={14} className="mr-1" />
            New Report
          </Button>
        </ToolbarRight>
      </Toolbar>

      {/* Table */}
      <div className="flex-1 overflow-hidden">
        {rows.length === 0 && !isLoading && !isError ? (
          <EmptyState
            title="No incident reports"
            description="File a new report using the button above."
          />
        ) : (
          <DataTable
            error={isError ? error : undefined}
            onRetry={() => void refetch()}
            errorSubject="warehouse incidents"
            columns={COLUMNS}
            data={rows}
            loading={isLoading}
            onRowClick={(row) =>
              navigate({ to: '/warehouse-incidents/$reportId', params: { reportId: row.id } })
            }
          />
        )}
      </div>
    </div>
  )
}
